import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { IntegrationHealthBadge } from "../components/shared/IntegrationHealth";
import { ShieldCheck, Download, Upload, Loader2, Clock, RefreshCw, AlertTriangle, FileText } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";

const SYSTEM_OPTIONS = [
  { key: "mastercontrol", label: "MasterControl", authType: "bearer" },
  { key: "etq_reliance", label: "ETQ Reliance", authType: "bearer" },
  { key: "trackwise", label: "TrackWise Digital (Honeywell)", authType: "oauth2" },
];

export default function QMSConfigPage({ onLogout, userRole, isFeatureEnabled }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [history, setHistory] = useState([]);
  const [qualityEvents, setQualityEvents] = useState([]);
  const [capa, setCapa] = useState([]);

  const fetchAll = useCallback(async () => {
    try {
      const [cfgResp, histResp] = await Promise.all([
        axios.get(`${API}/admin/qms-config`, { headers: getAuthHeaders() }),
        axios.get(`${API}/qms/sync-history`, { headers: getAuthHeaders() }),
      ]);
      setConfig(cfgResp.data);
      setHistory(histResp.data || []);
      // Fetch synced data
      axios.get(`${API}/qms/quality-events`, { headers: getAuthHeaders() }).then(r => setQualityEvents(r.data || [])).catch(() => {});
      axios.get(`${API}/qms/capa`, { headers: getAuthHeaders() }).then(r => setCapa(r.data || [])).catch(() => {});
    } catch { toast.error("Failed to load QMS config"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/qms-config`, config, { headers: getAuthHeaders() });
      toast.success("QMS configuration saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const handleSync = async (direction, dataType = "all") => {
    setSyncing(true);
    try {
      const resp = await axios.post(`${API}/qms/sync?direction=${direction}&data_type=${dataType}`, {}, { headers: getAuthHeaders() });
      const d = resp.data;
      toast.success(`Sync complete — Docs: ${d.documents}, Events: ${d.quality_events}, CAPA: ${d.capa}`);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(false); }
  };

  const update = (field, value) => setConfig(prev => ({ ...prev, [field]: value }));
  const selectedSystem = SYSTEM_OPTIONS.find(s => s.key === config?.system);

  if (loading || !config) {
    return <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}><div className="flex items-center justify-center h-64"><div className="text-[#64748B] font-mono">Loading QMS configuration...</div></div></Layout>;
  }

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="qms-config-page">
        <PageHeader title="Quality Management System" subtitle="Connect to MasterControl, ETQ Reliance, or TrackWise for compliance sync" testId="qms-title">
          <Button onClick={handleSave} disabled={saving} className="bg-[#2563EB] hover:bg-[#0F172A] text-white rounded-none" data-testid="qms-save-btn">
            {saving ? "Saving..." : "Save Configuration"}
          </Button>
        </PageHeader>

        <IntegrationHealthBadge
          integration="qms"
          onTestConnection={async () => {
            const resp = await axios.post(`${API}/admin/qms-test`, {}, { headers: getAuthHeaders() });
            return resp.data;
          }}
        />

        {/* System Selection */}
        <div className="bg-white border border-[#E2E8F0] p-6">
          <h3 className="font-bold text-sm text-[#0F172A] mb-4 flex items-center gap-2">
            <ShieldCheck size={16} /> QMS Platform
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            {SYSTEM_OPTIONS.map(sys => (
              <button
                key={sys.key}
                onClick={() => update("system", sys.key)}
                className={`p-4 border text-left transition-colors ${config.system === sys.key ? "border-[#2563EB] bg-[#EFF6FF]" : "border-[#E2E8F0] hover:border-[#94A3B8]"}`}
                data-testid={`qms-select-${sys.key}`}
              >
                <p className={`font-bold text-sm ${config.system === sys.key ? "text-[#2563EB]" : "text-[#0F172A]"}`}>{sys.label}</p>
                <p className="text-xs text-[#64748B] mt-1">Auth: {sys.authType === "bearer" ? "API Key" : "OAuth 2.0"}</p>
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3 mb-4">
            <Label className="text-sm font-medium w-24">Enabled</Label>
            <button onClick={() => update("enabled", !config.enabled)} className={`w-12 h-6 rounded-full transition-colors ${config.enabled ? "bg-[#10B981]" : "bg-[#CBD5E1]"}`} data-testid="qms-enabled-toggle">
              <div className={`w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${config.enabled ? "translate-x-6" : "translate-x-0.5"}`} />
            </button>
          </div>
        </div>

        {/* Connection Settings */}
        {config.system && (
          <div className="bg-white border border-[#E2E8F0] p-6">
            <h3 className="font-bold text-sm text-[#0F172A] mb-4">Connection — {selectedSystem?.label}</h3>
            <div className="space-y-4">
              <div>
                <Label className="text-sm">Server URL *</Label>
                <Input value={config.server_url || ""} onChange={(e) => update("server_url", e.target.value)} className="mt-1 rounded-none font-mono text-sm" placeholder="https://your-instance.mastercontrol.com" data-testid="qms-server-url" />
              </div>

              {selectedSystem?.authType === "bearer" ? (
                <div>
                  <Label className="text-sm">API Key / Bearer Token *</Label>
                  <Input type="password" value={config.api_key || ""} onChange={(e) => update("api_key", e.target.value)} className="mt-1 rounded-none font-mono text-sm" data-testid="qms-api-key" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Username</Label>
                    <Input value={config.username || ""} onChange={(e) => update("username", e.target.value)} className="mt-1 rounded-none font-mono text-sm" data-testid="qms-username" />
                  </div>
                  <div>
                    <Label className="text-sm">Password</Label>
                    <Input type="password" value={config.password || ""} onChange={(e) => update("password", e.target.value)} className="mt-1 rounded-none font-mono text-sm" data-testid="qms-password" />
                  </div>
                  <div>
                    <Label className="text-sm">Client ID</Label>
                    <Input value={config.client_id || ""} onChange={(e) => update("client_id", e.target.value)} className="mt-1 rounded-none font-mono text-sm" data-testid="qms-client-id" />
                  </div>
                  <div>
                    <Label className="text-sm">Client Secret</Label>
                    <Input type="password" value={config.client_secret || ""} onChange={(e) => update("client_secret", e.target.value)} className="mt-1 rounded-none font-mono text-sm" data-testid="qms-client-secret" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-sm">Token URL</Label>
                    <Input value={config.token_url || ""} onChange={(e) => update("token_url", e.target.value)} className="mt-1 rounded-none font-mono text-sm" placeholder="https://login.salesforce.com/services/oauth2/token" data-testid="qms-token-url" />
                  </div>
                </div>
              )}

              <div>
                <Label className="text-sm">API Base Path (optional override)</Label>
                <Input value={config.base_path || ""} onChange={(e) => update("base_path", e.target.value)} className="mt-1 rounded-none font-mono text-sm" placeholder={selectedSystem ? `Default: ${config.available_systems?.[config.system]?.default_base_path || ""}` : ""} data-testid="qms-base-path" />
              </div>
            </div>
          </div>
        )}

        {/* Sync Options */}
        {config.system && (
          <div className="bg-white border border-[#E2E8F0] p-6">
            <h3 className="font-bold text-sm text-[#0F172A] mb-4">Data Sync Options</h3>
            <div className="space-y-3">
              {[
                { key: "sync_documents", label: "Document Control", desc: "Sync blueprints ↔ QMS controlled documents" },
                { key: "sync_quality_events", label: "Quality Events / NCRs", desc: "Pull non-conformances, push overdue maintenance" },
                { key: "sync_capa", label: "CAPA Records", desc: "Pull corrective/preventive action records" },
                { key: "sync_audits", label: "Audit Findings", desc: "Pull audit results and findings" },
              ].map(opt => (
                <div key={opt.key} className="flex items-center justify-between p-3 border border-[#E2E8F0]" data-testid={`sync-opt-${opt.key}`}>
                  <div>
                    <p className="text-sm font-medium text-[#0F172A]">{opt.label}</p>
                    <p className="text-xs text-[#64748B]">{opt.desc}</p>
                  </div>
                  <button onClick={() => update(opt.key, !config[opt.key])} className={`w-10 h-5 rounded-full transition-colors ${config[opt.key] ? "bg-[#10B981]" : "bg-[#CBD5E1]"}`}>
                    <div className={`w-4 h-4 bg-white rounded-full transition-transform shadow-sm ${config[opt.key] ? "translate-x-5" : "translate-x-0.5"}`} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sync Controls */}
        {config.system && config.enabled && (
          <div className="bg-[#0F172A] p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-heading font-bold text-xl">Manual Sync</h2>
                <p className="text-[#94A3B8] text-sm mt-1">Pull quality data from {selectedSystem?.label} or push local records.</p>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => handleSync("pull")} disabled={syncing} className="bg-[#10B981] hover:bg-[#059669] text-white rounded-none" data-testid="qms-pull-btn">
                  {syncing ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Download size={14} className="mr-1" />}Pull from QMS
                </Button>
                <Button onClick={() => handleSync("push")} disabled={syncing} className="bg-[#D97706] hover:bg-[#B45309] text-white rounded-none" data-testid="qms-push-btn">
                  {syncing ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Upload size={14} className="mr-1" />}Push to QMS
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Quality Events */}
        {qualityEvents.length > 0 && (
          <div className="bg-white border border-[#E2E8F0] p-6">
            <h3 className="font-bold text-sm text-[#0F172A] mb-3 flex items-center gap-2"><AlertTriangle size={16} className="text-[#DC2626]" /> Quality Events ({qualityEvents.length})</h3>
            <div className="space-y-2">
              {qualityEvents.slice(0, 10).map(ev => (
                <div key={ev.id} className="flex items-center justify-between p-3 border border-[#E2E8F0]">
                  <div>
                    <p className="text-sm font-medium text-[#0F172A]">{ev.title}</p>
                    <p className="text-xs text-[#64748B]">{ev.event_type} — {ev.machine_number || "No machine"}</p>
                  </div>
                  <span className={`text-xs font-bold uppercase px-2 py-0.5 ${ev.severity === "high" ? "bg-[#FEF2F2] text-[#DC2626]" : ev.severity === "medium" ? "bg-[#FFFBEB] text-[#D97706]" : "bg-[#F0FDF4] text-[#10B981]"}`}>{ev.severity}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CAPA Records */}
        {capa.length > 0 && (
          <div className="bg-white border border-[#E2E8F0] p-6">
            <h3 className="font-bold text-sm text-[#0F172A] mb-3 flex items-center gap-2"><FileText size={16} className="text-[#2563EB]" /> CAPA Records ({capa.length})</h3>
            <div className="space-y-2">
              {capa.slice(0, 10).map(c => (
                <div key={c.id} className="flex items-center justify-between p-3 border border-[#E2E8F0]">
                  <div>
                    <p className="text-sm font-medium text-[#0F172A]">{c.title}</p>
                    <p className="text-xs text-[#64748B]">{c.capa_type} — Due: {c.due_date || "N/A"}</p>
                  </div>
                  <span className="text-xs font-bold uppercase px-2 py-0.5 bg-[#EFF6FF] text-[#2563EB]">{c.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sync History */}
        {history.length > 0 && (
          <div className="bg-white border border-[#E2E8F0] p-6">
            <h3 className="font-bold text-sm text-[#0F172A] mb-3 flex items-center gap-2"><Clock size={16} /> Sync History</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#FAFAFA] border-b border-[#E2E8F0]">
                  <tr>
                    {["Date", "System", "Direction", "Docs", "Events", "CAPA", "Errors"].map(h => (
                      <th key={h} className="px-3 py-2 text-left text-xs font-bold text-[#0F172A] uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]">
                  {history.slice(0, 20).map(h => (
                    <tr key={h.id} className="hover:bg-[#FAFAFA]">
                      <td className="px-3 py-2 font-mono text-xs text-[#64748B]">{new Date(h.created_at).toLocaleString()}</td>
                      <td className="px-3 py-2 text-xs font-bold uppercase">{h.system}</td>
                      <td className="px-3 py-2"><span className="px-2 py-0.5 text-xs font-bold bg-[#EFF6FF] text-[#1E40AF] uppercase">{h.direction}</span></td>
                      <td className="px-3 py-2 font-bold">{h.documents}</td>
                      <td className="px-3 py-2 font-bold">{h.quality_events}</td>
                      <td className="px-3 py-2 font-bold">{h.capa}</td>
                      <td className="px-3 py-2 text-[#DC2626]">{h.error_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
